// Search bar for landing page with category selection and recent searches
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './LandingSearchBar.module.css';
import { FaSearch, FaBuilding, FaHotel, FaConciergeBell, FaKey, FaHome, FaMicrophone, FaCamera, FaChevronDown, FaHistory, FaTimes, FaMapMarkerAlt } from 'react-icons/fa';

const LandingSearchBar = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isExpanded, setIsExpanded] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const categories = [
    { value: 'all', label: 'All Categories', icon: <FaSearch /> },
    { value: 'property', label: 'Properties', icon: <FaBuilding /> },
    { value: 'hotel', label: 'Hotels', icon: <FaHotel /> },
    { value: 'service', label: 'Services', icon: <FaConciergeBell /> },
    { value: 'rent', label: 'For Rent', icon: <FaKey /> },
    { value: 'buy', label: 'For Sale', icon: <FaHome /> }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'k') {
        e.preventDefault();
        const searchInput = searchRef.current?.querySelector(`.${styles.searchInput}`);
        if (searchInput) searchInput.focus();
      } else if (e.key === 'Escape') {
        setIsExpanded(false);
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search history
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
  }, []);

  const saveSearch = (query, category, loc) => {
    const newSearch = { query, category, location: loc || '', timestamp: Date.now() };
    const updatedSearches = [
      newSearch,
      ...recentSearches.filter(s => s.query !== query || s.location !== loc).slice(0, 4)
    ];
    setRecentSearches(updatedSearches);
    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim() || location.trim()) {
      saveSearch(searchTerm, selectedCategory, location);
      navigate('/search-results', {
        state: {
          query: searchTerm,
          category: selectedCategory,
          location
        }
      });
      setIsExpanded(false);
      setSearchTerm('');
      setLocation('');
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsDropdownOpen(false);
    if (searchTerm.trim() || location.trim()) {
      saveSearch(searchTerm, category, location);
      navigate('/search-results', {
        state: {
          query: searchTerm,
          category: category,
          location
        }
      });
      setIsExpanded(false);
      setSearchTerm('');
      setLocation('');
    }
  };

  const handleInputFocus = () => {
    setIsExpanded(true);
    setIsDropdownOpen(false);
  };

  const handleSuggestionClick = () => {
    handleSearch({ preventDefault: () => {} });
  };

  const handleRecentSearchClick = (search) => {
    setSearchTerm(search.query);
    setLocation(search.location || '');
    setSelectedCategory(search.category);
    navigate('/search-results', {
      state: {
        query: search.query,
        category: search.category,
        location: search.location || ''
      }
    });
    setIsExpanded(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setLocation('');
    setIsExpanded(false);
  };

  const selectedCategoryObj = categories.find(cat => cat.value === selectedCategory);

  return (
    <div 
      className={`${styles.landingSearchContainer} ${isExpanded ? styles.expanded : ''}`} 
      ref={searchRef}
    >
      <form className={styles.landingSearchForm} onSubmit={handleSearch}>
        <div 
          className={styles.categorySelector}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <span className={styles.categoryIcon}>{selectedCategoryObj.icon}</span>
          <span className={styles.categoryLabel}>{selectedCategoryObj.label}</span>
          <FaChevronDown className={styles.categoryArrow} />
        </div>

        <div className={styles.searchInputWrapper}>
          <input
            type="text"
            placeholder="Search properties, hotels, services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={handleInputFocus}
            className={styles.searchInput}
          />
          {searchTerm && (
            <button 
              type="button" 
              className={styles.clearSearchBtn}
              onClick={clearSearch}
              aria-label="Clear search"
            >
              <FaTimes />
            </button>
          )}
        </div>

        <div className={styles.locationInputWrapper}>
          <FaMapMarkerAlt className={styles.locationIcon} />
          <input
            type="text"
            placeholder="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onFocus={handleInputFocus}
            className={styles.locationInput}
          />
        </div>

        <div className={styles.searchActions}>
          {/* <button 
            type="button" 
            className={styles.voiceSearchBtn}
            aria-label="Voice search"
          >
            <FaMicrophone />
          </button>
          <button 
            type="button" 
            className={styles.imageSearchBtn}
            aria-label="Image search"
          >
            <FaCamera />
          </button> */}
          <button type="submit" className={styles.searchSubmitBtn} aria-label="Search">
            <FaSearch />
          </button>
        </div>

        {isDropdownOpen && (
          <div className={styles.categoryDropdown}>
            {categories.map((category) => (
              <button
                key={category.value}
                type="button"
                className={`${styles.categoryOption} ${selectedCategory === category.value ? styles.selected : ''}`}
                onClick={() => handleCategorySelect(category.value)}
              >
                <span className={styles.optionIcon}>{category.icon}</span>
                <span className={styles.optionLabel}>{category.label}</span>
              </button>
            ))}
          </div>
        )}
      </form>

      {isExpanded && (
        <div className={styles.searchSuggestions}>
          {searchTerm || location ? (
            <button 
              className={styles.suggestionItem}
              onClick={handleSuggestionClick}
            >
              <FaSearch className={styles.suggestionIcon} />
              <span>Search for "{searchTerm}"{location ? ` in ${location}` : ''} ({selectedCategoryObj.label})</span>
            </button>
          ) : recentSearches.length > 0 ? (
            <>
              <div className={styles.suggestionHeader}>Recent Searches</div>
              {recentSearches.map((search, index) => (
                <button 
                  key={index} 
                  className={styles.suggestionItem}
                  onClick={() => handleRecentSearchClick(search)}
                >
                  <FaHistory className={styles.suggestionIcon} />
                  <span>{search.query || 'All'} {search.location ? `in ${search.location}` : ''} ({categories.find(c => c.value === search.category)?.label})</span>
                </button>
              ))}
            </>
          ) : (
            <div className={styles.noSuggestions}>
              Start typing to search for properties, hotels, or services
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LandingSearchBar;